export const EVENT_CARD_GRID_RESIZE_DEBOUNCE_MS = 1000;
export const EVENT_CARD_GRID_DESKTOP_GAP = 16;
export const EVENT_CARD_GRID_MOBILE_GAP = 12;
export const EVENT_CARD_GRID_DESKTOP_MIN_WIDTH = 220;
export const EVENT_CARD_GRID_MOBILE_MIN_WIDTH = 156;
export const EVENT_CARD_PAGE_SIZE_MAX = 200;

const toPositiveInteger = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  const integer = Math.floor(number);
  return integer > 0 ? integer : null;
};

export const isValidEventPageSize = (value) => {
  const integer = toPositiveInteger(value);
  return Boolean(integer && integer <= EVENT_CARD_PAGE_SIZE_MAX);
};

export const normalizeEventPageSize = (value, fallback) => {
  const integer = toPositiveInteger(value);
  if (integer && integer <= EVENT_CARD_PAGE_SIZE_MAX) return integer;
  return fallback;
};

export const getEventCardGridMetrics = (
  containerWidth,
  { isCompact = false, minCardWidth: minCardWidthOverride, gap: gapOverride } = {},
) => {
  const width = Number(containerWidth);
  const gap = Number.isFinite(gapOverride)
    ? gapOverride
    : (isCompact ? EVENT_CARD_GRID_MOBILE_GAP : EVENT_CARD_GRID_DESKTOP_GAP);
  const minCardWidth = Number.isFinite(minCardWidthOverride)
    ? minCardWidthOverride
    : (isCompact ? EVENT_CARD_GRID_MOBILE_MIN_WIDTH : EVENT_CARD_GRID_DESKTOP_MIN_WIDTH);

  if (!Number.isFinite(width) || width <= 0) {
    return {
      columnCount: isCompact ? 2 : 1,
      gap,
      minCardWidth,
    };
  }

  if (isCompact) {
    const canFitTwo = width >= minCardWidth * 2 + gap;
    return {
      columnCount: canFitTwo ? 2 : 1,
      gap,
      minCardWidth,
    };
  }

  return {
    columnCount: Math.max(1, Math.floor((width + gap) / (minCardWidth + gap))),
    gap,
    minCardWidth,
  };
};

export const roundEventCardPageSize = (preferredSize, columnCount) => {
  const preferred = normalizeEventPageSize(preferredSize, 20);
  const columns = Math.max(1, toPositiveInteger(columnCount) || 1);
  const floorRows = Math.floor(preferred / columns);
  const ceilRows = Math.ceil(preferred / columns);
  const floorMultiple = Math.max(columns, floorRows * columns);
  const ceilMultiple = Math.max(columns, ceilRows * columns);
  const floorDiff = Math.abs(preferred - floorMultiple);
  const ceilDiff = Math.abs(ceilMultiple - preferred);
  return ceilDiff <= floorDiff ? ceilMultiple : floorMultiple;
};

export const buildEventCardPageSizeOptions = (baseOptions, columnCount) => {
  const seen = new Set();
  const options = [];
  (Array.isArray(baseOptions) ? baseOptions : []).forEach((base) => {
    const rounded = roundEventCardPageSize(base, columnCount);
    if (!isValidEventPageSize(rounded) || seen.has(rounded)) return;
    seen.add(rounded);
    options.push(rounded);
  });
  return options.sort((a, b) => a - b);
};

export const resolveClosestEventPageSize = (currentSize, options, fallback = 20) => {
  const validOptions = (Array.isArray(options) ? options : [])
    .map((option) => normalizeEventPageSize(option, null))
    .filter(Boolean);
  if (!validOptions.length) return normalizeEventPageSize(currentSize, fallback);

  const current = normalizeEventPageSize(currentSize, fallback);
  if (validOptions.includes(current)) return current;

  return validOptions.reduce((best, option) => {
    const bestDiff = Math.abs(best - current);
    const optionDiff = Math.abs(option - current);
    if (optionDiff < bestDiff) return option;
    if (optionDiff === bestDiff && option > best) return option;
    return best;
  }, validOptions[0]);
};
